import React, { useCallback } from 'react';
import { Box, Typography, Paper, Button, alpha, useTheme } from '@mui/material';
import { ArrowForward, ChatBubbleOutline, VerifiedUser } from '@mui/icons-material';
import type { EnhancedQuizResult } from './quizTypes';
import type { InsightData } from './hooks';
import {
  StockInsightCard,
  DividendInsightCard,
  MomentumInsightCard,
  RiskInsightCard,
} from './components';
import { useAuth } from '../../contexts/AuthContext';
import analyticsService from '../../services/analytics';

interface QuizResultScreenProps {
  result: EnhancedQuizResult;
  /** Live insight data for preview cards */
  insights?: InsightData[];
  /** Whether insight data is still loading */
  insightsLoading?: boolean;
  /** Whether we have at least one insight with live data */
  hasLiveData?: boolean;
  onCreateAccount: () => void;
  onExplore: (route: string) => void;
}

const QuizResultScreen: React.FC<QuizResultScreenProps> = ({
  result,
  insights = [],
  insightsLoading = false,
  hasLiveData = false,
  onCreateAccount,
  onExplore,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const topRoute = result.recommendations[0]?.route ?? '/';

  // Login-first flow: if unauthenticated, store destination and prompt login
  const handleInsightCardClick = useCallback(
    (route: string, insightType?: string) => {
      const requiresLogin = !user;

      if (insightType) {
        analyticsService.trackQuizInsightClicked(insightType, route, requiresLogin);
      } else {
        analyticsService.trackQuizCtaClicked('recommendation_clicked', route);
      }

      if (requiresLogin) {
        // Store destination for post-login redirect
        localStorage.setItem('discovery_quiz_redirect', route);
        onCreateAccount(); // Opens login dialog
      } else {
        onExplore(route);
      }
    },
    [user, onCreateAccount, onExplore]
  );

  // Handle "Explore first" click - store session for return tracking
  const handleExploreFirst = useCallback(() => {
    // Store explore session for deferred conversion tracking
    try {
      localStorage.setItem(
        'explore_first_session',
        JSON.stringify({
          timestamp: Date.now(),
          route: topRoute,
        })
      );
    } catch {
      // Ignore localStorage errors
    }

    analyticsService.trackQuizCtaClicked('explore_first', topRoute);
    onExplore(topRoute);
  }, [topRoute, onExplore]);
  const { profileSummary } = result;

  // Show insight section if we have live data or are still loading
  const showInsightSection = hasLiveData || insightsLoading || insights.length > 0;

  // Persona-specific subtitles for better targeting
  const personaSubtitles: Record<string, string> = {
    builder: "Let's build your portfolio with confidence",
    manager: 'Your portfolio insights are ready',
    researcher: 'Your research toolkit is configured',
  };

  const subtitle =
    personaSubtitles[profileSummary.persona.id] || 'Personalized for your investment profile';

  return (
    <Box>
      {/* Headline */}
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}
      >
        {result.headline}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {subtitle}
      </Typography>

      {/* Profile Summary Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.default',
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', fontSize: 10, letterSpacing: 1.2, mb: 1.5, display: 'block' }}
        >
          Your Investment Profile
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {([
            ['Profile', profileSummary.persona.label],
            ['Goal', profileSummary.goal.label],
            ['Risk', profileSummary.risk.label],
            ['Timeline', profileSummary.horizon.label],
          ] as const).map(([label, value]) => (
            <Box key={label}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
        {profileSummary.needs.length > 0 && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mt: 1.5, fontSize: 11 }}
          >
            Interests: {profileSummary.needs.map((n) => n.label).join(', ')}
          </Typography>
        )}
      </Paper>

      {/* Live Insight Cards (NEW) */}
      {showInsightSection && (
        <>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}
          >
            Live insights for you
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
            {insights.map((insight) => {
              const { config, quote, ratios } = insight;
              const key = `${config.type}-${config.symbol}`;

              switch (config.type) {
                case 'top_pick':
                  return (
                    <StockInsightCard
                      key={key}
                      config={config}
                      quote={quote}
                      isLoading={insightsLoading}
                      onNavigate={(route) => handleInsightCardClick(route, 'top_pick')}
                    />
                  );
                case 'dividend':
                  return (
                    <DividendInsightCard
                      key={key}
                      config={config}
                      quote={quote}
                      ratios={ratios}
                      isLoading={insightsLoading}
                      onNavigate={(route) => handleInsightCardClick(route, 'dividend')}
                    />
                  );
                case 'momentum':
                  return (
                    <MomentumInsightCard
                      key={key}
                      config={config}
                      quote={quote}
                      isLoading={insightsLoading}
                      onNavigate={(route) => handleInsightCardClick(route, 'momentum')}
                    />
                  );
                case 'risk_snapshot':
                  return (
                    <RiskInsightCard
                      key={key}
                      config={config}
                      quote={quote}
                      isLoading={insightsLoading}
                      onNavigate={(route) => handleInsightCardClick(route, 'risk_snapshot')}
                    />
                  );
                default:
                  return null;
              }
            })}
          </Box>
        </>
      )}

      {/* Toolkit Header */}
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}
      >
        Your recommended starting path
      </Typography>

      {/* Recommendation Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
        {result.recommendations.map((rec) => (
          <Paper
            key={rec.route}
            elevation={0}
            onClick={() => handleInsightCardClick(rec.route)}
            sx={{
              p: 1.5,
              cursor: 'pointer',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'text.primary',
                transform: 'translateX(4px)',
              },
            }}
          >
            {/* Step Number */}
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: 'text.primary',
                color: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 700,
                flexShrink: 0,
                mt: 0.25,
              }}
            >
              {rec.step}
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {rec.title}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25, lineHeight: 1.4 }}>
                {rec.personalizedBenefit}
              </Typography>
            </Box>

            {/* Arrow */}
            <ArrowForward
              sx={{
                fontSize: 18,
                color: 'text.secondary',
                opacity: 0,
                transition: 'opacity 0.2s',
                mt: 0.25,
                '.MuiPaper-root:hover &': { opacity: 1 },
              }}
            />
          </Paper>
        ))}
      </Box>

      {/* AI Assistant Note */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <ChatBubbleOutline sx={{ fontSize: 16, color: 'text.disabled' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          AI Assistant is available on every page to answer your questions
        </Typography>
      </Box>

      {/* Premium Trust Indicator */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          mb: 1.5,
        }}
      >
        <VerifiedUser sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.3 }}>
          Institutional-grade analysis • Personalized for you
        </Typography>
      </Box>

      {/* Primary CTA - Premium Positioning */}
      <Button
        fullWidth
        variant="contained"
        onClick={onCreateAccount}
        sx={{
          py: 1.5,
          bgcolor: 'text.primary',
          color: 'background.paper',
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': { opacity: 0.85 },
        }}
      >
        Secure My Profile
      </Button>

      {/* Value reinforcement */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textAlign: 'center',
          color: 'text.disabled',
          mt: 1,
          fontSize: 11,
        }}
      >
        Your institutional-grade investment insights are ready
      </Typography>

      <Button
        fullWidth
        onClick={handleExploreFirst}
        sx={{
          mt: 1.5,
          py: 1,
          color: 'text.secondary',
          textTransform: 'none',
          fontWeight: 500,
          '&:hover': {
            bgcolor: alpha(theme.palette.text.primary, 0.04),
          },
        }}
      >
        Explore first
      </Button>
    </Box>
  );
};

export default QuizResultScreen;
